/**
 * What an expanded engagement row shows: its **ownership** and its **delivery
 * elements** (f7.1).
 *
 * The Ownership card is here rather than on a detail screen because there IS
 * no detail screen -- an engagement has no route and no navigation entry. The
 * expansion is the only surface an engagement has, so it is where a Project
 * Head is named. It is **f4.2's component, generalised by f5.1 and reused**:
 * this feature passes its own node type and adds no third copy, and the card's
 * role selector offers `PROJECT_HEAD` and nothing else because it asks the
 * node-type registry what the level supports.
 *
 * Without it, `POST api/delivery-role-assignments` with
 * `node_type: "ENGAGEMENT"` would be enabled server-side -- which is the whole
 * obligation f4.2 wrote into an error message naming this feature -- and
 * unreachable from the application.
 *
 * ---
 *
 * adr-17 placed this surface here rather than in a fifth navigation entry:
 * *"expanding an engagement lists its elements with code, name, status and
 * origin and offers confirm and deactivate. No separate Elements screen and no
 * navigation entry."* Its own reasoning is the test of the design -- *"an
 * element has no meaning outside its engagement, which is the plan's own
 * reason for keeping engagements out of the navigation"* -- so the same
 * reasoning applies one level down.
 *
 * **No pagination, no search and no virtualisation**, and that is sized by a
 * measurement rather than by taste. adr-17 says feed 01 holds *"5,266 known
 * since 2017"*; 5,266 is its ROW count and the distinct element count is
 * **354**, with zero references whose duplicate rows differ in any of the
 * seven columns. So an expansion renders **at most 28** rows -- `KE052453` --
 * and **exactly one** for 79 of 141 engagements. Controls sized for 5,266 rows
 * would be built for data that does not exist.
 *
 * **Fetched on open, not with the collection.** The query is `enabled` only
 * while the row is expanded, which is what keeps a tab nobody has expanded at
 * one request rather than eight. Embedding the elements in the engagement
 * collection would make the common case pay for the rare one.
 *
 * **The list is empty in every release before f10.4**, and the empty state
 * says so rather than showing a zero-row table: no route creates an element,
 * because elements are discovered from the Kimble feed.
 */

import { Ban, Check, RotateCcw } from 'lucide-react';

import type { Engagement, EngagementElement } from '@/api/client';
import {
  useConfirmElement,
  useDeactivateElement,
  useEngagementElements,
  useLookupCatalog,
  useReactivateElement,
} from '@/api/queries';
import { OwnershipCard } from '@/ownership/OwnershipCard';
import { DELIVERY_ROLE_TYPE } from '@/routes/business-unit-groups/ownershipView';
import { EmptyCell } from '@/ui/EmptyCell';

import {
  ENGAGEMENT_ROLE_CODES,
  actionsFor,
  badgeClassFor,
  formatMoment,
  generalMessage,
} from './engagementView';

/** The level an engagement's own assignments are recorded against. */
const NODE_TYPE = 'ENGAGEMENT';

export function ElementExpansion({
  engagement,
  clientId,
  mayManage,
}: {
  engagement: Engagement;
  /** Whose engagement collection carries the `element_count` a verb changes. */
  clientId: string;
  mayManage: boolean;
}) {
  const catalog = useLookupCatalog();
  const deliveryRoles =
    catalog.data?.types.find((type) => type.code === DELIVERY_ROLE_TYPE)
      ?.values ?? [];

  return (
    <div className="detail-columns">
      <div className="stack-sm">
        <h4 className="fieldset-title">Delivery elements</h4>
        <ElementTable
          engagement={engagement}
          clientId={clientId}
          mayManage={mayManage}
        />
      </div>
      {/*
        f4.2's card, parameterised rather than copied. Its role selector offers
        PROJECT_HEAD only, because the node-type registry says that is what
        this level supports -- so it is correct by construction and this
        feature changed nothing inside it.
      */}
      <OwnershipCard
        nodeType={NODE_TYPE}
        nodeId={engagement.id}
        nodeName={engagement.name}
        roleCodes={ENGAGEMENT_ROLE_CODES}
        deliveryRoles={deliveryRoles}
        mayManage={mayManage}
        emptyHint="No Project Head is named on this engagement yet. Any Atlas account can be assigned here, including one nobody has signed in to yet."
      />
    </div>
  );
}

function ElementTable({
  engagement,
  clientId,
  mayManage,
}: {
  engagement: Engagement;
  clientId: string;
  mayManage: boolean;
}) {
  // `enabled` defaults to true and this component only mounts while the row is
  // open, so mounting IS the "on expansion" trigger.
  const elements = useEngagementElements(engagement.id);

  if (elements.isPending) {
    return (
      <div
        className="stack-sm"
        aria-busy="true"
        aria-label="Loading the delivery elements"
      >
        {[0, 1].map((index) => (
          <span key={index} className="skeleton-bar group-skeleton-line" />
        ))}
      </div>
    );
  }

  if (elements.error) {
    return (
      <p className="hint">
        The delivery elements of {engagement.code} could not be loaded.
      </p>
    );
  }

  const rows = elements.data?.elements ?? [];

  if (rows.length === 0) {
    /*
     * Not a zero-row table. An engagement with no elements is the NORMAL state
     * in every release before f10.4, and saying where they come from is more
     * useful than an empty grid -- the prototype's own dialog footer says the
     * same thing: "Delivery elements are discovered from the Kimble feed, not
     * entered here."
     */
    return (
      <p className="hint">
        No delivery elements yet. They arrive with the Kimble engagement feed
        and are confirmed here &mdash; they are never entered by hand.
      </p>
    );
  }

  return (
    <table className="data compact">
      <thead>
        <tr>
          {/* adr-17's four fields, and the two the verbs act on. */}
          <th scope="col">Element</th>
          <th scope="col">Name</th>
          <th scope="col">Origin</th>
          <th scope="col">Status</th>
          <th scope="col">Confirmed</th>
          <th scope="col">
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((element) => (
          <ElementRow
            key={element.id}
            element={element}
            engagementId={engagement.id}
            clientId={clientId}
            mayManage={mayManage}
          />
        ))}
      </tbody>
    </table>
  );
}

function ElementRow({
  element,
  engagementId,
  clientId,
  mayManage,
}: {
  element: EngagementElement;
  engagementId: string;
  clientId: string;
  mayManage: boolean;
}) {
  const confirm = useConfirmElement();
  const deactivate = useDeactivateElement();
  const reactivate = useReactivateElement();

  const busy =
    confirm.isPending || deactivate.isPending || reactivate.isPending;
  const failure =
    generalMessage(confirm.error) ??
    generalMessage(deactivate.error) ??
    generalMessage(reactivate.error);
  const actions = actionsFor(element);
  const target = { engagementId, elementId: element.id, clientId };

  return (
    <>
      <tr>
        <td className="mono">{element.code}</td>
        <td>
          {/*
            Normally null, and NOT a gap in the data load: feed 01 has seven
            columns and none is an element name. Rendered through the shared
            empty cell, never as a fabricated label and never by substituting
            the engagement's name. f14.1 is the first feature that can fill it.
          */}
          {element.name ?? <EmptyCell />}
        </td>
        <td>
          <span className="badge badge-chip">{element.origin.name}</span>
        </td>
        <td>
          <span className={badgeClassFor(element.status.code)}>
            {element.status.name}
          </span>
        </td>
        <td>
          {element.confirmed_at ? (
            <span className="hint">
              {formatMoment(element.confirmed_at)}
              {element.confirmed_by ? ` · ${element.confirmed_by.display}` : ''}
            </span>
          ) : (
            /*
             * A distinct state from Inactive, and adr-17 lists both because
             * both render: confirmation is whether the element has been
             * ACCEPTED, status is whether it is IN SERVICE.
             */
            <span className="badge pill-warning">Not confirmed</span>
          )}
        </td>
        <td>
          <div className="row-actions">
            {mayManage && (
              <>
                {actions.confirm && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-icon"
                    title="Confirm this element"
                    aria-label={`Confirm ${element.code}`}
                    disabled={busy}
                    onClick={() => confirm.mutate(target)}
                  >
                    <Check aria-hidden="true" />
                  </button>
                )}
                {actions.deactivate && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-icon"
                    title="Deactivate this element"
                    aria-label={`Deactivate ${element.code}`}
                    disabled={busy}
                    onClick={() => deactivate.mutate(target)}
                  >
                    <Ban aria-hidden="true" />
                  </button>
                )}
                {actions.reactivate && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-icon"
                    title="Reactivate this element"
                    aria-label={`Reactivate ${element.code}`}
                    disabled={busy}
                    onClick={() => reactivate.mutate(target)}
                  >
                    <RotateCcw aria-hidden="true" />
                  </button>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
      {failure && (
        /*
         * Rendered against the ROW rather than as a toast, following f4.1's
         * rule: a refusal belongs beside the thing it refused. The verbs name
         * no field, so this is the envelope's `message`.
         */
        <tr>
          <td colSpan={6}>
            <span className="field-error" role="alert">
              {failure}
            </span>
          </td>
        </tr>
      )}
    </>
  );
}
